import api from "../api.js";
import CheckoutBasketItem from "./CheckoutBasketItem.js";
import CheckoutBasketPayment from "./CheckoutBasketPayment.js";

/**
 * Checkout service
 *
 * Enables interactions with the HOPS checkout API.
 */
class CheckoutService {

	/**
	 * Add items to a checkout basket.
	 *
	 * Creates a new basket when `basketId` is `null`.
	 *
	 * @param {Array<Object>} items Basket item objects
	 * @param {Integer} optional basketId Basket ID to add to
	 * @param {Integer} optional orderSource Order source to identify as (ID)
	 * @param {Boolean} optional getQuestions Request to include the basket's checkout questions in the response
	 * @param {Integer} optional orgId Organisation ID to use for searches e.g. automatic discounts
	 * @return {Promise} Resolves with Checkout Basket API data
	 */
	static addBasketItems(items, basketId=null, orderSource=null, getQuestions=false, orgId=null) {
		return api.call({
			url: "/api/checkout/basket",
			method: "POST",
			data: {
				Id: basketId,
				Items: items.map(i => {
					return {
						Uuid: i.Uuid,
						RelatedItemUuid: i.RelatedItemUuid,
						Item: CheckoutBasketItem.getBasketApiData(i),
						OrderableType: i.OrderableType,
						Price: i.Price,
						Quantity: i.Quantity
					};
				}),
				Source: orderSource,
				GetCheckoutQuestions: getQuestions,
				OrgId: orgId
			}
		}).then(({data}) => data);
	}


	/**
	 * Remove items from a checkout basket.
	 * 
	 * @param {Array<Object>} items Basket item objects to remove
	 * @param {Integer} basketId Basket ID containing the items
	 * @param {Boolean} optional getQuestions Request to include the basket's checkout questions in the response
	 * @return {Promise} Resolves with Checkout Basket API data
	 */
	static removeBasketItems(items, basketId, getQuestions=false) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/items`,
			method: "DELETE",
			data: {
				Items: items.map(i => i.Uuid),
				GetCheckoutQuestions: getQuestions
			}
		}).then(({data}) => data);
	}


	/**
	 * Update the quantity of an item claim in a checkout basket.
	 *
	 * The item must actually already exist in the basket!
	 *
	 * @param {String} item Item claim UUID to update
	 * @param {Integer} quantity New item quantity
	 * @param {Integer} basketId Basket ID containing the item
	 * @return {Promise} Resolves with Checkout Basket API data
	 */
	static updateBasketItemQuantity(item, quantity, basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/items/${item}/quantity`,
			method: "PUT",
			data: {Quantity: quantity}
		}).then(({data}) => data);
	}


	/**
	 * Update the price of an item claim in a checkout basket.
	 *
	 * The item must actually already exist in the basket!
	 * The supervisor's PIN will be verified in the backend
	 * with password_verify().
	 *
	 * @param {String} item Item claim UUID to update
	 * @param {Integer} price New item price
	 * @param {String} reason Reason for the price change
	 * @param {Integer} basketId Basket ID containing the item
	 * @param {Integer} supervisorUserId User ID of a supervisor
	 * @param {Integer} supervisorPin PIN of a supervisor
	 * @return {Promise} Resolves with Checkout Basket API data
	 */
	static updateBasketItemPrice(item, price, reason, basketId, supervisorUserId, supervisorPin) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/items/${item}/price`,
			method: "PUT",
			data: {
				Price: price,
				Reason: reason,
				SupervisorUserId: supervisorUserId,
				SupervisorPin: supervisorPin
			}
		}).then(({data}) => data);
	}


	/**
	 * Extend the lifetime of a basket.
	 *
	 * @param {Integer} basketId
	 * @return {Promise} Resolves with Checkout Basket API data
	 */
	static extendBasketLifetime(basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/ping`,
			method: "POST"
		}).then(({data}) => data);
	}


	/**
	 * Get order questions to ask the customer for items in a given basket.
	 *
	 * @param {Integer} basketId
	 * @return {Promise} Resolves with API data
	 */
	static getBasketQuestions(basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/questions`,
			method: "GET"
		}).then(({data}) => data);
	}


	/**
	 * Add payment items to a checkout basket.
	 */
	static addPaymentItems(payments, basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/payments`,
			method: "POST",
			data: {
				Payments: payments.map(p => {
					return {
						Uuid: p.Uuid,
						Moto: p.Moto,
						PaymentMethod: p.PaymentMethod?.Id,
						PaymentPed: p.PaymentPed,
						PaymentProcessor: p.PaymentProcessor,
						PaymentProcessorData: CheckoutBasketPayment.getPaymentApiData(p),
						PaymentType: p.PaymentType,
						Value: p.Value
					};
				})
			}
		}).then(({data}) => data);
	}


	/**
	 * Remove payment items from a checkout basket.
	 * 
	 * THIS MIGHT NOT BE WHAT YOU WANT TO DO
	 * 
	 * If a payment was made in error and is to be refunded, use the Cancel method
	 * below so there's still a record of it taking place (receipts, etc.) and the
	 * payment processor can perform cleanup.
	 */
	static removePaymentItems(payments, basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/payments`,
			method: "DELETE",
			data: {
				Payments: payments.map(i => i.Uuid)
			}
		}).then(({data}) => data);
	}


	/**
	 * Cancel payment items from a checkout basket.
	 * 
	 * Use this method if a payment was made in error and is to be refunded
	 * so there's still a record of it taking place (receipts, etc.) and the
	 * payment processor can perform cleanup.
	 */
	static cancelPaymentItems(item, basketId) {
		return api.call({
			url: `/api/checkout/basket/${basketId}/payments/${item}/cancel`,
			method: "DELETE"
		}).then(({data}) => data);
	}

}

export default CheckoutService;
